import { type Component, Match, Show, Switch, createMemo, createUniqueId } from 'solid-js'
import { FIELD_MESSAGE_METRICS, FIELD_MESSAGE_SLOT, FieldError } from '../../primitives/FieldError'

export interface RangeSliderProps {
  value: number | null
  onChange: (value: number) => void
  min: number
  max: number
  step?: number
  disabled?: boolean
  label?: string
  error?: string
  class?: string
  unsetHint?: string
  unsetValueText?: string
  'aria-labelledby'?: string
  'aria-describedby'?: string | undefined
}

const SLIDER_KEYS = new Set([
  'ArrowLeft',
  'ArrowRight',
  'ArrowUp',
  'ArrowDown',
  'Home',
  'End',
  'PageUp',
  'PageDown',
])

const THUMB_RADIUS_PX = 14
const NARROWEST_TRACK_PX = 280
const LABEL_DIGIT_PX = 8
const LABEL_GAP_PX = 4

function edgeLabelReservePercent(valueLabel: string, edgeLabel: string): number {
  const reachFromEdge =
    (valueLabel.length * LABEL_DIGIT_PX) / 2 +
    edgeLabel.length * LABEL_DIGIT_PX +
    LABEL_GAP_PX -
    THUMB_RADIUS_PX
  return (Math.max(0, reachFromEdge) / (NARROWEST_TRACK_PX - 2 * THUMB_RADIUS_PX)) * 100
}

function stepNearestMiddle(min: number, max: number, step: number): number {
  const range = max - min
  if (range <= 0 || step <= 0) {
    return min
  }
  const steps = Math.min(Math.round(range / 2 / step), Math.floor(range / step))
  return min + steps * step
}

export const RangeSlider: Component<RangeSliderProps> = (props) => {
  const inputId = createUniqueId()
  const errorId = createUniqueId()
  const step = () => props.step ?? 1
  const isSet = () => props.value !== null

  const percentage = createMemo(() => {
    const range = props.max - props.min
    if (props.value === null || range <= 0) {
      return 0
    }
    return Math.min(100, Math.max(0, ((props.value - props.min) / range) * 100))
  })

  const hidesMinLabel = () =>
    isSet() && percentage() <= edgeLabelReservePercent(String(props.value), String(props.min))
  const hidesMaxLabel = () =>
    isSet() && 100 - percentage() <= edgeLabelReservePercent(String(props.value), String(props.max))

  const describedBy = () =>
    [props['aria-describedby'], props.error ? errorId : null].filter(Boolean).join(' ') || undefined

  const mutedWhenDisabled = (enabledClass: string) =>
    props.disabled ? 'text-[color:var(--color-text-muted)]' : enabledClass

  const trackStateClass = () => {
    const classes: string[] = []
    if (props.error) {
      classes.push('bg-[var(--color-error-100)] inset-ring inset-ring-[var(--color-error)]')
    } else {
      classes.push('bg-[var(--color-neutral-200)]')
    }
    if (!isSet()) {
      classes.push(
        'has-[input:focus-visible]:outline-2 has-[input:focus-visible]:outline-offset-4 has-[input:focus-visible]:outline-[var(--color-primary-500)]'
      )
    }
    if (props.disabled) {
      classes.push('opacity-50')
    }
    return classes.join(' ')
  }

  const commitWhenUnset = (input: HTMLInputElement) => {
    if (props.value === null) {
      props.onChange(Number(input.value))
    }
  }

  return (
    <div
      data-range-slider
      data-disabled={props.disabled ? '' : undefined}
      class={`flex flex-col gap-1.5 ${props.class ?? ''}`}
    >
      <Show when={props.label}>
        <label
          for={inputId}
          class={`mb-0.5 block text-sm font-medium ${mutedWhenDisabled('text-[color:var(--color-text)]')}`}
        >
          {props.label}
        </label>
      </Show>

      <div
        data-range-track
        data-invalid={props.error ? '' : undefined}
        class={`group relative my-2 h-3 rounded-[var(--radius-md)] ${trackStateClass()}`}
      >
        <Show
          when={isSet()}
          fallback={
            <div class="pointer-events-none absolute inset-y-0 inset-x-3.5">
              <div
                data-range-ghost
                aria-hidden="true"
                class="absolute left-1/2 top-1/2 w-7 h-7 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-dashed border-[var(--color-text-subtle)] bg-[var(--color-surface)]"
              />
            </div>
          }
        >
          <div class="pointer-events-none absolute inset-y-0 inset-x-3.5">
            <div
              data-range-fill
              class="absolute inset-y-0 -left-3.5 rounded-[var(--radius-md)] bg-[var(--color-primary-500)]"
              style={{ right: `${100 - percentage()}%` }}
            />
            <div
              data-range-thumb="set"
              class="absolute top-1/2 w-7 h-7 -translate-x-1/2 -translate-y-1/2 rounded-full border-[3px] border-[var(--color-primary-500)] bg-[var(--color-surface)] shadow-[var(--shadow-md)] group-has-[input:focus-visible]:ring-2 group-has-[input:focus-visible]:ring-[var(--color-primary-500)] group-has-[input:focus-visible]:ring-offset-2 group-has-[input:focus-visible]:ring-offset-[var(--color-surface)]"
              style={{ left: `${percentage()}%` }}
            />
          </div>
        </Show>

        <input
          id={inputId}
          data-range-input
          type="range"
          min={props.min}
          max={props.max}
          step={step()}
          value={props.value ?? stepNearestMiddle(props.min, props.max, step())}
          disabled={props.disabled}
          aria-labelledby={props['aria-labelledby']}
          aria-describedby={describedBy()}
          aria-invalid={props.error ? true : undefined}
          aria-valuetext={
            props.value === null ? (props.unsetValueText ?? props.unsetHint) : undefined
          }
          onInput={(e) => props.onChange(Number(e.currentTarget.value))}
          onPointerUp={(e) => commitWhenUnset(e.currentTarget)}
          onKeyUp={(e) => {
            if (SLIDER_KEYS.has(e.key)) {
              commitWhenUnset(e.currentTarget)
            }
          }}
          class="absolute inset-x-0 top-1/2 h-11 -translate-y-1/2 w-full m-0 p-0 opacity-0 cursor-pointer disabled:cursor-not-allowed"
        />
      </div>

      <div class="relative flex min-h-5 items-baseline justify-between gap-3 text-xs tabular-nums text-[color:var(--color-text-muted)]">
        <span data-range-min class={hidesMinLabel() ? 'invisible' : undefined}>
          {props.min}
        </span>
        <span data-range-max class={hidesMaxLabel() ? 'invisible' : undefined}>
          {props.max}
        </span>
        <Show when={isSet()}>
          <div class="pointer-events-none absolute inset-y-0 inset-x-3.5">
            <span
              data-range-value
              class={`absolute top-0 -translate-x-1/2 whitespace-nowrap font-semibold ${mutedWhenDisabled('text-[color:var(--color-text)]')}`}
              style={{ left: `${percentage()}%` }}
            >
              {props.value}
            </span>
          </div>
        </Show>
      </div>

      <Show when={props.error || props.unsetHint}>
        <div data-range-message class={FIELD_MESSAGE_SLOT}>
          <Switch>
            <Match when={props.error}>
              <FieldError id={errorId}>{props.error}</FieldError>
            </Match>
            <Match when={!isSet() && props.unsetHint}>
              <p
                data-range-unset-hint
                class={`${FIELD_MESSAGE_METRICS} text-[color:var(--color-text-muted)]`}
              >
                {props.unsetHint}
              </p>
            </Match>
          </Switch>
        </div>
      </Show>
    </div>
  )
}
